import React from 'react';
import { Button } from '@repo/ui/Button';
import { Dialog } from '@repo/ui/Dialog';
import { DialogClose } from '@repo/ui/Dialog/DialogClose';
import { DialogContent } from '@repo/ui/Dialog/DialogContent';
import { DialogHeader } from '@repo/ui/Dialog/DialogHeader';
import { DialogRoot } from '@repo/ui/Dialog/DialogRoot';
import { DialogTitle } from '@repo/ui/Dialog/DialogTitle';
import { DialogTrigger } from '@repo/ui/Dialog/DialogTrigger';
import { Flex } from '@repo/ui/Flex';
import { IconButton } from '@repo/ui/IconButton';
import { ItemCard } from '../ItemCard/ItemCard';
import { Text, rawString } from '../Language/Language';
import type { OrderItem, Claim } from '../../graphql/query/getOrder';
import s from './ItemsPreview.module.css';

type ItemWithReason = OrderItem & {
  reason?: {
    title: string;
    code: string;
    retailerReasonCode?: string;
    retailerReasonCodeId?: string;
  };
  claim?: Claim | null;
};

export type ItemsPreviewProps = {
  items: ItemWithReason[];
  itemCountText: string;
  viewDetailsText: string;
  dialogTitle: string;
  variant?: 'bordered' | 'clean';
};

export const ItemsPreview = ({
  items,
  itemCountText,
  viewDetailsText,
  dialogTitle,
  variant = 'clean',
}: ItemsPreviewProps) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className={s.previewContainer} data-variant={variant}>
      <div className={s.previewHeader}>
        <span className={s.itemCountText}>{itemCountText}</span>
        <DialogRoot>
          <DialogTrigger asChild={true}>
            <Button variant="plain">{viewDetailsText}</Button>
          </DialogTrigger>
          <Dialog>
            <DialogHeader className={s.modalHeader}>
              <DialogTitle position="center">{dialogTitle}</DialogTitle>
              <DialogClose asChild={true}>
                <IconButton icon="close" label="Close" />
              </DialogClose>
            </DialogHeader>
            <DialogContent className={s.modalContent}>
              <div className={s.modalItemList}>
                {items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <Flex direction="column">
                      <ItemCard
                        item={item}
                        imageSize="sm"
                        className={s.modalItemCard}
                        containerClassName={s.modalItemCardWrapper}
                        claim={item.claim}
                        additionalDetails={[
                          {
                            name:
                              rawString({
                                langKey:
                                  'pdx.reason_selection.shipment_preview.quantity',
                              }) ?? 'Quantity',
                            value: '1',
                          },
                        ]}
                      />
                    </Flex>
                    {index < items.length - 1 && (
                      <div className={s.itemSeparator} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </DialogRoot>
      </div>
      <div className={s.thumbnailRow}>
        {items.slice(0, 4).map((item) => (
          <div
            key={item.id}
            className={s.thumbnail}
            data-testid="itemsPreviewItem"
          >
            {item.image?.url ? (
              <img
                src={item.image.url}
                alt={item.title}
                className={s.thumbnailImage}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div
                className={s.placeholderThumbnail}
                role="img"
                aria-label={
                  rawString({
                    langKey: 'pdx.item_selection.no_image',
                    fallback: 'No image available',
                  }) ?? 'No image available'
                }
              >
                <Text langKey="pdx.item_selection.no_image" />
              </div>
            )}
          </div>
        ))}
        {items.length > 4 && (
          <div className={s.moreItemsIndicator}>+{items.length - 4}</div>
        )}
      </div>
    </div>
  );
};
